import { Alert, Button, Grid, InputAdornment } from '@mui/material';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormTextInput } from 'src/components/FormPart/FormTextInput';
import { FormNumberInput } from 'src/components/FormPart/FormNumberInput';
import { CreateAdminCompanyFormData, AdminCompany } from '../../types';
import { useAdminCompaniesApi, useCompaniesApi } from '../../api';
import { buildRequestData, buildDefaultCampaignInfoPayload, errorAlertSx } from '../../helpers';
import { CompanyPhoneType } from '../../api/CompanyPhoneApi';
import { useGetCampaignFlyerApi } from 'src/api/campaignRegistry';
import { CreateCompanySubscriptionData } from './AddSubscriptionStep';

export interface InviteUserFormProps {
  onBack: () => void;
  companyFormData: CreateAdminCompanyFormData | null;
  subscriptionData: CreateCompanySubscriptionData | null;
  inviteUserData: InviteUserData | null;
  createdCompany: AdminCompany | null;
  onInviteUserDataChange: (data: InviteUserData) => void;
  onCompanyCreated: (company: AdminCompany) => void;
  onSubmit: (
    company: AdminCompany,
    userData: InviteUserData,
    nextAction?: (company: AdminCompany) => unknown | Promise<unknown>
  ) => Promise<void>;
  reloadCompanies: () => Promise<void>;
  reloadUserCompanies: () => Promise<void>;
}

export interface InviteUserData {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export const InviteUserForm: FC<InviteUserFormProps> = ({
  onSubmit,
  companyFormData,
  subscriptionData,
  inviteUserData,
  createdCompany,
  onInviteUserDataChange,
  onCompanyCreated,
  reloadCompanies,
  reloadUserCompanies,
  onBack,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyCreationError, setCompanyCreationError] = useState<string | null>(null);
  const { createAdminCompany } = useCompaniesApi();
  const {
    createAdminCompanyPhoneNumber,
    createAdminCompanySubscription,
    saveCompanyBrandInfo,
    saveCompanyCampaignInfo,
  } = useAdminCompaniesApi();
  const getCampaignFlyer = useGetCampaignFlyerApi();

  const form = useForm<InviteUserData>({
    defaultValues: inviteUserData || {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: undefined,
    },
  });

  const handleSubmit = async (data: InviteUserData) => {
    if (!companyFormData) return;

    onInviteUserDataChange(data);
    setIsSubmitting(true);
    try {
      let company: AdminCompany;

      // Use existing company if it was already created, otherwise create new one
      if (createdCompany) {
        company = createdCompany;
      } else {
        // Create the company first
        const requestData = buildRequestData(companyFormData);
        const companyResponse = await createAdminCompany(requestData);
        company = companyResponse.data;
        // Notify parent that company was created
        onCompanyCreated(company);
      }

      const companyId = company.id;

      // Refresh user companies list and update companies slice right after company creation
      try {
        await reloadCompanies();
      } catch (refreshCompaniesError) {
        console.error('Failed to refresh user companies after company creation:', refreshCompaniesError);
      }

      try {
        await reloadUserCompanies();
      } catch (refreshUserCompaniesError) {
        console.error('Failed to refresh header companies after company creation:', refreshUserCompaniesError);
      }

      // Try to create phone number
      if (companyFormData.phoneNumberType && companyFormData.phoneNumber) {
        try {
          const phoneNumber = `+1${companyFormData.phoneNumber}`;
          await createAdminCompanyPhoneNumber(
            companyId,
            phoneNumber,
            companyFormData.phoneNumberType as CompanyPhoneType,
          );

          // Phone creation succeeded, now create subscription if provided
          if (subscriptionData) {
            await createAdminCompanySubscription(
              companyId,
              subscriptionData.planId,
              subscriptionData.startType === 'date' ? (subscriptionData.startDate ?? '') : '',
              subscriptionData.autoRenew,
              subscriptionData.startType,
            );
          }

          // For 10DLC numbers, create brand and campaign
          if (companyFormData.phoneNumberType === CompanyPhoneType._10dlc_local || companyFormData.phoneNumberType === CompanyPhoneType._10dlc_hosted) {
            try {
              // Create brand
              const brandRequest = {
                entityType: companyFormData.entityType!,
                displayName: companyFormData.name,
                phone: phoneNumber,
                email: data.email,
                brandRelationship: companyFormData.brandRelationship!,
                vertical: companyFormData.vertical!,
                mock: false,
              };

              await saveCompanyBrandInfo(companyId, brandRequest);

              // Generate flyer
              const flyerResponse = await getCampaignFlyer(companyId);

              // Create campaign
              const campaignRequest = buildDefaultCampaignInfoPayload(
                companyFormData.name,
                phoneNumber,
                data.email,
                companyFormData.useCase,
                companyFormData.subUseCases,
                flyerResponse.data.url,
              );

              await saveCompanyCampaignInfo(companyId, campaignRequest);

              console.log('Brand and campaign created successfully for 10DLC number');
            } catch (registryError) {
              console.error('Failed to create brand/campaign for 10DLC number:', registryError);
            }
          }
        } catch (error) {
          const phoneFailedDependencyErrorCode = 424;
          const defaultErrorMessage = 'Please check the phone number and try again';
          
          if (error.code === phoneFailedDependencyErrorCode) {
            console.error('Failed to create phone number for company:', error);
            setCompanyCreationError(`Failed to create phone number: ${error instanceof Error ? error.message : defaultErrorMessage}`);
          } else {
            setCompanyCreationError(`${error instanceof Error ? error.message : defaultErrorMessage}`);
          }

          setIsSubmitting(false);
          return;
        }
      }
      await onSubmit(company, data, reloadCompanies);
    } catch (error) {
      console.error('Failed to create company and invite user:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <FormTextInput
            form={form}
            fieldName="firstName"
            label="First Name"
            placeholder="Enter First Name"
            validation={{
              required: "First Name is required",
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <FormTextInput
            form={form}
            fieldName="lastName"
            label="Last Name"
            placeholder="Enter Last Name"
            validation={{
              required: "Last Name is required",
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormTextInput
            form={form}
            fieldName="email"
            label="Email"
            placeholder="Enter Email"
            validation={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address"
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormNumberInput
            form={form}
            format={{ style: 'phoneNumber' }}
            max={9999999999}
            fieldName="phoneNumber"
            label="Phone Number"
            placeholder="Enter Phone Number"
            validation={{
              validate: (value) => {
                if (value && String(value).length !== 10) {
                  return 'Phone number must be exactly 10 digits';
                }
                return true;
              },
            }}
            inputProps={{
              startAdornment: <InputAdornment position="start">+1</InputAdornment>,
            }}
          />
        </Grid>

        {companyCreationError && (
          <Grid item xs={12}>
            <Alert severity="error" sx={errorAlertSx}>
              {companyCreationError}
            </Alert>
          </Grid>
        )}

      <Grid item xs={12} display="flex" justifyContent="space-between">
          <Button
            type="button"
            onClick={() => {
              onInviteUserDataChange(form.getValues());
              onBack();
            }}
            variant="outlined"
            color="secondary"
            size="large"
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={!form.formState.isValid || isSubmitting}
            variant="contained"
            color="secondary"
            size="large"
          >
            {isSubmitting ? 'Creating' : 'Create'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
